import { ISODateString } from 'types/date'
import { NullableValue, OptionalValue } from 'types/utils'
import { Phone } from 'types/phone'
import { DateOfBirth } from 'types/date'
import { MemberStatus } from 'types/Member/member'
import { isObject } from 'util/type-guards'
import PendingVisit from 'types/Visit/PendingVisit'
import VisitTypes from 'constants/visit-types'

// Common base interface for all crisis stream states
export interface CrisisStreamBase {
  status: string
  streamId: string
  callId: string
  crisisInitiatedAt: ISODateString
  callNotes?: string
  callLocation?: string
  reasonForVisit?: string
}

// Member information that appears in multiple states
export interface CrisisMemberInfo {
  firstName: string
  lastName: string
  email: string
  dob: DateOfBirth
  gender?: NullableValue<OptionalValue<string>>
  groupId: string
  memberId: string
}

// Initial state
export interface CrisisStreamInitiated extends CrisisStreamBase {
  phoneNumber?: NullableValue<OptionalValue<Phone>>
}

// Member assigned state - adds member information
export interface CrisisMemberAssigned extends CrisisStreamBase {
  phoneNumber?: NullableValue<OptionalValue<Phone>>
  member: CrisisMemberInfo
  memberAssignedAt: ISODateString
}

// Customer assigned state - adds customer information
export interface CrisisCustomerAssigned extends CrisisStreamBase {
  clientId: string
  groupId: string
  callbackPhone: Phone
  member?: CrisisMemberInfo
  memberAssignedAt: ISODateString
}

// Caller verified state - adds member information
export interface CrisisCallerVerified extends CrisisStreamBase {
  member: CrisisMemberInfo
  memberLookupRequestedAt?: ISODateString
}

// Caller duplicate found state - adds duplicate member information
export interface CrisisCallerDuplicateFound extends CrisisStreamBase {
  memberLookupRequestedAt: ISODateString
  duplicateMemberFoundAt?: ISODateString
  duplicateMember: {
    duplicateMemberId: string
    clientId: string
    groupId: string
    status: MemberStatus
  }
  requestedMember: CrisisMemberInfo
}

// Visit queueing state - adds visit information
export interface CrisisVisitQueueing extends CrisisStreamBase {
  member?: CrisisMemberInfo
  memberAssignedAt?: ISODateString
  memberLookupRequestedAt?: ISODateString
  clientId?: string
  groupId?: string
  callbackPhone?: Phone
  visit: {
    visitId: string
    providerId?: string
    providerName?: string
  }
  visitRequestedAt: ISODateString
}

// Visit queued state - adds queued visit information
export interface CrisisVisitQueued extends CrisisStreamBase {
  member?: CrisisMemberInfo
  memberAssignedAt?: ISODateString
  memberLookupRequestedAt?: ISODateString
  clientId?: string
  groupId?: string
  callbackPhone?: Phone
  visit: {
    visitId: string
    visitQueuedAt: ISODateString
    providerId?: string
    providerName?: string
  }
}

// Composite type that can represent any state of the crisis stream

export interface CallParticipant {
  id: string
  name: string
  phone: string
  role: string
  connectedAt: string
}
export interface CrisisStreamState extends CrisisStreamBase {
  // Base properties from CrisisStreamBase are always present
  agentId?: string
  serviceAdminId?: string | number
  // Member properties - optional as they may not be present in all states
  member?: CrisisMemberInfo
  memberAssignedAt?: ISODateString
  memberLookupRequestedAt?: ISODateString
  memberId?: string

  // Customer properties - optional as they may not be present in all states
  clientId?: NullableValue<OptionalValue<string>>
  callParticipants?: {
    connected?: CallParticipant[]
    disconnected?: CallParticipant[]
  }
  groupId?: NullableValue<OptionalValue<string>>
  customerAssignedAt?: ISODateString // customer assigned at is when both client and group are assigned
  clientAssignedAt?: ISODateString
  groupAssignedAt?: ISODateString
  groupsIdentified?: { groupId: string; name: string }[]

  callerPhone?: NullableValue<OptionalValue<Phone>>
  customerPhone?: NullableValue<OptionalValue<Phone>>
  callbackPhone?: NullableValue<OptionalValue<Phone>>
  phoneNumber?: NullableValue<OptionalValue<Phone>>

  // Duplicate member properties - optional as they may not be present in all states
  duplicateMemberId?: string
  duplicateMember?: {
    duplicateMemberId: string
    clientId: string
    groupId: string
    status: MemberStatus
  }
  duplicateMemberFoundAt?: ISODateString
  requestedMember?: CrisisMemberInfo

  // Visit properties - optional as they may not be present in all states
  visit?: {
    visitId: string
    providerId?: string
    providerName?: string
    reasonForVisit?: string
    location?: string
    notes?: string
    phoneNumber?: Phone
    groupId?: string
    memberId?: string
  }
  visitId?: string
  visitRequestedAt?: ISODateString
  visitQueueingAt?: ISODateString
  visitQueuedAt?: ISODateString
  providerId?: string
  providerAssignedAt?: ISODateString
  providerUnassignedAt?: ISODateString
  providerPhone?: Phone
  providerPhoneAssignedAt?: ISODateString
}

// Define more specific narrowed types for each state
export type CrisisStreamWithMember = CrisisStreamState & {
  member: CrisisMemberInfo
  memberAssignedAt: ISODateString
  phoneNumber: NullableValue<OptionalValue<Phone>>
}

export type CrisisStreamWithCustomer = CrisisStreamState & {
  clientId: string
  groupId?: string
  callbackPhone?: Phone
  member?: CrisisMemberInfo
  memberAssignedAt: ISODateString
}

export type CrisisStreamWithGroup = CrisisStreamWithCustomer & {
  groupId: string
}

export type CrisisStreamWithVerifiedCaller = CrisisStreamState & {
  member: CrisisMemberInfo
  memberLookupRequestedAt?: ISODateString
}

export type CrisisStreamWithDuplicateCaller = CrisisStreamState & {
  duplicateMember: {
    duplicateMemberId: string
    clientId: string
    groupId: string
    status: MemberStatus
  }
  memberLookupRequestedAt: ISODateString
  requestedMember: CrisisMemberInfo
}

export type CrisisStreamWithQueueingVisit = CrisisStreamState & {
  visit: {
    visitId: string
  }
  visitRequestedAt: ISODateString
}

export type CrisisStreamWithQueuedVisit = CrisisStreamState & {
  visit: {
    visitId: string
    providerId: string
  }
  visitQueuedAt: ISODateString
}

export type CrisisStreamWithProviderPhone = CrisisStreamState & {
  visit: {
    visitId: string
    providerId: string
  }
  visitQueuedAt: ISODateString
}

// Unified response type for all crisis stream API responses
export interface CrisisStreamResponse {
  stream: CrisisStreamState
}

// Type guards to check the state of a crisis stream with proper type narrowing
export const isCrisisMemberAssigned = (event: any): event is CrisisStreamWithMember => {
  return (
    isObject(event) &&
    'member' in event &&
    isObject(event.member) &&
    'memberId' in event &&
    'streamId' in event &&
    'callId' in event
  )
}

export const isCrisisCustomerAssigned = (event: any): event is CrisisStreamWithCustomer => {
  return isObject(event) && 'clientId' in event && 'streamId' in event && 'callId' in event
}

export const isCrisisGroupAssigned = (event: any): event is CrisisStreamWithGroup => {
  return isObject(event) && 'clientId' in event && 'groupId' in event && 'streamId' in event && 'callId' in event
}

export const isCrisisCallerVerified = (event: any): event is CrisisStreamWithVerifiedCaller => {
  return (
    isObject(event) && 'member' in event && 'streamId' in event && 'callId' in event && !('duplicateMember' in event)
  )
}

export const isCrisisCallerDuplicateFound = (event: any): event is CrisisStreamWithDuplicateCaller => {
  return (
    isObject(event) &&
    'duplicateMember' in event &&
    'requestedMember' in event &&
    'streamId' in event &&
    'callId' in event
  )
}

export const isCrisisVisitQueueing = (event: any): event is CrisisStreamWithQueueingVisit => {
  return (
    isObject(event) &&
    'visit' in event &&
    isObject(event.visit) &&
    'visitRequestedAt' in event &&
    'streamId' in event &&
    'callId' in event
  )
}

export const isCrisisVisitQueued = (event: any): event is CrisisStreamWithQueuedVisit => {
  return (
    isObject(event) &&
    'visit' in event &&
    isObject(event.visit) &&
    'visitId' in event.visit &&
    'streamId' in event &&
    'callId' in event
  )
}

export const isCrisisVisit = (visit: PendingVisit) => {
  return visit?.visitType === VisitTypes.crisis
}

export const isCrisisProviderPhoneVerified = (event: any): event is CrisisStreamWithProviderPhone => {
  return (
    isObject(event) &&
    'providerId' in event &&
    'providerPhone' in event &&
    event.providerPhone !== null &&
    'providerPhoneAssignedAt' in event &&
    event.providerPhoneAssignedAt !== null
  )
}
